/**
 * ユーザーラベルの2段目（表示項目の値）を組み立てる計算。
 *
 * 仕様: 吹き出しは2行。1行目はユーザー名、2行目は表示項目の1番目の値。
 * 「1番目」はシステム設定 `user_disp_item` の順の1番目で、利用者側に並べ替えのUIは無く
 * 非表示の除外のみ。したがって、表示中の項目の並びの先頭がそのまま2段目になる。
 *
 * 値の文字列化とアイコンの対応は `UserLabelItemText.js` が持つ（i18nや共通ライブラリを使うため）。
 * ここは他のモジュールに依存しない計算だけを持つ。
 */

import { hsl } from 'd3-color'
import ColorUtil from '@/js/util/ColorUtil.js'

/**
 * ラベルの1行に出せる文字数の上限
 * 仕様: ユーザー名32文字がラベルの最大サイズ。超える分は省略する
 * @type {Number}
 */
export const MAX_LABEL_LENGTH = 32

/**
 * クラスターにまとまった点のリストに出すユーザー名の文字数の上限
 * 仕様（cluster.png）: 最大12文字。超えたら `...` で省略する（マウスホバーは不要）
 * @type {Number}
 */
export const MAX_CLUSTER_NAME_LENGTH = 12

/**
 * 表示項目の値が無いときに2段目へ出す文字
 * 2026-08-20 の打ち合わせで、値が無くても2段目は出すと決まった（決定3-2）
 * @type {String}
 */
export const EMPTY_ITEM_TEXT = '-'

/**
 * ステイタスの色の丸に枠線を付けるかどうかの境目
 * リストの詳細カードと同じ値。7だと枠線が付きすぎるため調整されている
 * @type {Number}
 */
const DOT_CONTRAST_THRESHOLD = 1.5

/**
 * 背景色に対して見分けがつく枠線の色を返す
 *
 * ステイタスの色が背景に近いと丸が見えなくなるため、
 * リストの詳細カードと同じ判定で、暗くした色を枠線にする
 * @param {String} color ステイタスの色
 * @param {String} backgroundColor 背景色
 * @returns {String} 枠線の色（不要なら元の色をそのまま返す）
 */
export const getDotBorderColor = (color, backgroundColor) => {
  if (!color || !backgroundColor) return color
  const ratio = (a, b) => {
    const la = ColorUtil.calcRelativeLuminance(a)
    const lb = ColorUtil.calcRelativeLuminance(b)
    const [l1, l2] = la > lb ? [la, lb] : [lb, la]
    return (l1 + 0.05) / (l2 + 0.05)
  }
  try {
    let current = color
    let value = ratio(current, backgroundColor)
    // 色を暗くしても近づかない場合に備えて回数を区切る
    for (let i = 0; i < 20 && value < DOT_CONTRAST_THRESHOLD; i++) {
      current = hsl(current).darker().formatHex()
      value = ratio(current, backgroundColor)
    }
    return current
  } catch {
    // 色の形式が想定外でもラベル自体は描けるようにする
    return color
  }
}

/**
 * 色をrgb配列にする
 *
 * 色はマスタの自由入力なので、想定外の形式だと変換で例外が出る。
 * ラベル全体が描けなくなるのを避けるため、失敗したら色を付けない
 * @param {String} color 色
 * @returns {Array.<Number>|null} rgb配列
 */
export const toRgb = (color) => {
  if (!color) return null
  try {
    return ColorUtil.hexToRgb(color)
  } catch {
    return null
  }
}

/**
 * 2段目に出す表示項目を探す
 *
 * 表示項目は `"item_type:id"` の形式で、ユーザーごとの値も同じ形式のキーを持つ
 * @param {Object} user ユーザーの状態
 * @param {String} dispItem 表示項目のキー（表示中の並びの先頭）
 * @returns {Object|null} 表示項目の値
 */
export const findLabelItem = (user, dispItem) => {
  if (!dispItem) return null
  const items = user?.additional_items
  if (!Array.isArray(items)) return null
  return items.find((item) => item?.item_type === dispItem) || null
}

/**
 * 文字数を上限で省略する
 * @param {String} text 文字列
 * @param {Number} maxLength 上限の文字数
 * @returns {String} 省略後の文字列
 */
export const truncate = (text, maxLength) => {
  const value = `${text ?? ''}`
  if (!maxLength || maxLength < 0) return value
  // 絵文字などは2つ分として数えられるため、文字単位に分けてから数える
  const chars = Array.from(value)
  if (chars.length <= maxLength) return value
  return `${chars.slice(0, maxLength).join('')}...`
}

/**
 * ラベルの文字列を組み立てる
 *
 * 仕様（cluster.png）: **ユーザー名32文字がラベルの最大サイズ**で、
 * それを超える任意項目は `...` で省略する。
 * したがって**省略するのは2段目だけで、ユーザー名は切らない**。
 *
 * 2段目に出すものが無いときは1行のまま返す。
 * 空行を作るとアイコンの位置と吹き出しの高さがずれるため、改行自体を入れない
 * @param {String} userName ユーザー名
 * @param {String} itemText 表示項目の値
 * @param {Number} maxLength 2段目の文字数の上限
 * @returns {String} ラベルの文字列（2行のときは改行区切り）
 */
export const buildLabelText = (
  userName,
  itemText,
  maxLength = MAX_LABEL_LENGTH
) => {
  // 値に改行が入ると3行以上になり、アイコンの位置と吹き出しの高さが崩れる
  const name = `${userName ?? ''}`.replace(/\s*\n\s*/g, ' ')
  const value = `${itemText ?? ''}`.replace(/\s*\n\s*/g, ' ').trim()
  if (!value) return name
  return `${name}\n${truncate(value, maxLength)}`
}
